import { default as React } from 'react';
import { useState, useCallback } from 'react';
import { makeStyles } from '@mui/styles';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
  },
  inputWithChips: {
    '& .MuiOutlinedInput-root': {
      padding: theme.spacing(0.5),
      paddingRight: theme.spacing(1),
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(0.5),
      alignItems: 'center',
      backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#fff',
      minHeight: '56px',
    },
    '& .MuiOutlinedInput-input': {
      flex: 1,
      minWidth: '120px',
      padding: theme.spacing(1),
    },
  },
  labelChip: {
    height: '28px',
    fontSize: '0.85rem',
    fontWeight: 500,
    backgroundColor: theme.palette.mode === 'dark' ? '#424242' : '#e0e0e0',
    color: theme.palette.mode === 'dark' ? '#fff' : '#424242',
    border: `1px solid ${
      theme.palette.mode === 'dark' ? '#616161' : '#bdbdbd'
    }`,
    '& .MuiChip-deleteIcon': {
      color:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.7)'
          : 'rgba(0, 0, 0, 0.6)',
      '&:hover': {
        color: theme.palette.mode === 'dark' ? '#fff' : '#000',
      },
    },
  },
  helperText: {
    marginTop: theme.spacing(1),
    fontSize: '0.875rem',
  },
  suggestedLabels: {
    marginTop: theme.spacing(2),
  },
  suggestedLabel: {
    margin: theme.spacing(0.5),
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[4],
    },
  },
  suggestedTitle: {
    fontSize: '0.875rem',
    fontWeight: 500,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
}));

const SUGGESTED_LABELS = [
  'terraform',
  'template',
  'argocd',
  'atlas',
  'dotnet',
  'microservice',
  'infrastructure',
  'documentation',
  'kubernetes',
  'helm',
];

export const LabelsField = ({
  onChange,
  formData,
  schema,
  uiSchema,
}: FieldExtensionComponentProps<string[]>) => {
  const classes = useStyles();
  const [labels, setLabels] = useState<string[]>(formData || []);
  const [inputValue, setInputValue] = useState('');

  // Use schema.title if provided, otherwise default to "Labels"
  const fieldTitle = schema?.title || 'Labels';
  const placeholder =
    uiSchema?.['ui:placeholder'] ||
    'Type a label and press Enter or click suggested labels below';

  const handleAddLabel = useCallback(
    (label: string) => {
      const trimmedLabel = label.trim().toLowerCase();
      if (trimmedLabel && !labels.includes(trimmedLabel)) {
        const newLabels = [...labels, trimmedLabel];
        setLabels(newLabels);
        onChange(newLabels);
        setInputValue('');
      }
    },
    [labels, onChange],
  );

  const handleRemoveLabel = useCallback(
    (labelToRemove: string) => {
      const newLabels = labels.filter(l => l !== labelToRemove);
      setLabels(newLabels);
      onChange(newLabels);
    },
    [labels, onChange],
  );

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleAddLabel(inputValue);
      }
    },
    [inputValue, handleAddLabel],
  );

  const availableSuggestedLabels = SUGGESTED_LABELS.filter(
    label => !labels.includes(label),
  );

  return (
    <FormControl fullWidth className={classes.root}>
      <TextField
        className={classes.inputWithChips}
        variant="outlined"
        label={fieldTitle}
        placeholder={placeholder}
        value={inputValue}
        onChange={e => setInputValue(e.target.value)}
        onKeyPress={handleKeyPress}
        InputProps={{
          startAdornment: labels.map(label => (
            <Chip
              key={label}
              label={label}
              onDelete={() => handleRemoveLabel(label)}
              className={classes.labelChip}
              size="small"
            />
          )),
        }}
      />

      {availableSuggestedLabels.length > 0 && (
        <div className={classes.suggestedLabels}>
          <Typography className={classes.suggestedTitle}>Suggested:</Typography>
          <Box display="flex" flexWrap="wrap">
            {availableSuggestedLabels.map(label => (
              <Chip
                key={label}
                label={label}
                onClick={() => handleAddLabel(label)}
                className={`${classes.labelChip} ${classes.suggestedLabel}`}
                size="small"
              />
            ))}
          </Box>
        </div>
      )}

      {schema.description && (
        <FormHelperText className={classes.helperText}>
          {schema.description}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export const LabelsFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'LabelsField',
    component: LabelsField,
  }),
);

// Alias for topics (same component, different semantic meaning)
export const TopicsFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'TopicsField',
    component: LabelsField,
  }),
);
